import * as type from '../mutation-types';
import { createActionHelpers } from 'vuex-loading';
import Vue from 'vue';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loader',
});

const TYPE_INVENTORY = 1;
const TYPE_COMPOSITE = 0;

const variantModel = {
    name: null,
    sku: null,
    barcode: null,
    cost: null,
    price: '0.00',
    stock: {
        continue_selling: null,
        composites: [],
        inventories: [],
        end_products: [],
    },
};

const productModel = {
    id: null,
    name: null,
    description: null,
    track_stock: false,
    use_variant: false,
    inventory_type: TYPE_INVENTORY,
    variants: [_.cloneDeep(variantModel)],
    tags: [],
};

const mapToClientObject = async product => {
    const componentIds = [];

    product.variants.map(v => {
        v.stock.composites.forEach(c => {
            componentIds.push(c.composite_stock_id);
        });
    });

    if (!componentIds.length) return product;

    const options = {
        params: {
            ids: componentIds,
        },
    };

    const { data } = await axios.get(Laroute.route('data_api.stocks'), options);

    product.variants.map(v => {
        v.stock.composites.forEach(c => {
            const stock = data.find(stock => stock.id == c.composite_stock_id);
            Vue.set(c, 'composite_stock', stock);
        });
    });

    return product;
};

const mapToServerObject = product => {
    const serverObject = _.pick(product, [
        'name',
        'code',
        'description',
        'use_variant',
        'inventory_type',
    ]);

    serverObject.track_stock = product.track_stock ? 1 : 0;
    serverObject.tags = product.tags.map(tag => tag.id);
    serverObject.variants = [];

    product.variants.forEach(v => {
        const variant = _.pick(v, ['cost', 'sku', 'price', 'default']);

        if (v.id > 0) {
            variant.id = v.id;
        }

        // if not using variant ignore the name input
        if (product.use_variant) {
            variant.name = v.name;
        } else {
            // set default name for variant
            if (_.isNull(v.name) || v.name.trim() == '')
                variant.name = 'Regular';
        }

        // process stock input
        if (product.track_stock) {
            variant.stock = { continue_selling: false };

            // track  barcode when type is inventory
            if (product.inventory_type == TYPE_INVENTORY) {
                variant.barcode = v.barcode;
            }

            if (v.stock.id > 0) {
                variant.stock.id = v.stock.id;
            }

            // if type inventory
            if (product.inventory_type === TYPE_INVENTORY) {
                variant.stock.inventories = [];

                v.stock.inventories.forEach(inv => {
                    const inventory = _.pick(inv, [
                        'outlet_id',
                        'quantity',
                        'low_stock_level',
                        'ideal_stock_level',
                    ]);

                    // set low_stock_level = 0 if user not set the input
                    if (
                        _.isNull(inventory.low_stock_level) ||
                        _.isNaN(parseFloat(inventory.low_stock_level))
                    )
                        inventory.low_stock_level = 0;

                    // set ideal_stock_level = 0 if user not set the input
                    if (
                        _.isNull(inventory.ideal_stock_level) ||
                        _.isNaN(parseFloat(inventory.low_stock_level))
                    )
                        inventory.ideal_stock_level = 0;

                    if (inv.id > 0) {
                        inventory.id = inv.id;
                    }

                    variant.stock.inventories.push(inventory);
                });
            }

            if (product.inventory_type === TYPE_COMPOSITE) {
                variant.stock.composites = [];

                v.stock.composites.forEach(composite => {
                    const compositeInput = _.pick(composite, [
                        'composite_stock_id',
                        'composite_potion',
                    ]);

                    variant.stock.composites.push(compositeInput);
                });
            }
        }

        serverObject.variants.push(variant);
    });

    return serverObject;
};

export default {
    namespaced: true,
    state: {
        product: productModel,
        original: _.cloneDeep(productModel),
        decrementalId: -1,
    },
    getters: {
        product: state => state.product,
        original: state => state.original,
        originalHasInventories: state => {
            const variants = state.original.variants;
            return (
                variants.filter(v => v.stock.inventories.length > 0).length > 0
            );
        },
        originalHasComposites: state => {
            const variants = state.original.variants;
            return (
                variants.filter(v => v.stock.composites.length > 0).length > 0
            );
        },
        trackStock: state => state.product.track_stock,
        isTypeInventory: state =>
            state.product.inventory_type == TYPE_INVENTORY,
        isTypeComposite: state =>
            state.product.inventory_type == TYPE_COMPOSITE,

        // variants
        variants: state => state.product.variants,
        findVariantIndex: state => variant_id => {
            return _.findIndex(state.product.variants, v => v.id == variant_id);
        },
        findVariant: state => variant_id => {
            return _.find(state.product.variants, v => v.id == variant_id);
        },
        findOutletInventoryIndex: (state, getters) => (
            outlet_id,
            variant_id,
        ) => {
            const variant = getters.findVariant(variant_id);
            return _.findIndex(
                variant.stock.inventories,
                inventory => inventory.outlet_id == outlet_id,
            );
        },
        findOutletInventory: (state, getters) => (
            outlet_id,
            variant_id,
            return_empty = false,
        ) => {
            const variant = getters.findVariant(variant_id);
            const outletStock = _.find(
                variant.stock.inventories,
                inventory => inventory.outlet_id == outlet_id,
            );

            if (_.isNil(outletStock) && return_empty) {
                return {
                    id: null,
                    outlet_id,
                    quantity: null,
                    low_stock_level: null,
                    ideal_stock_level: null,
                    stock_id: variant.stock.id,
                };
            }

            return outletStock;
        },

        isCompositeComponent(state) {
            const variants = state.product.variants;

            for (let i = 0; i < variants.length; i++) {
                if (variants[i].stock.end_products.length > 0) {
                    return true;
                }
            }

            return false;
        },
    },
    mutations: {
        [type.SET_PRODUCT](state, product) {
            state.product = product;
        },

        [type.SET_ORIGINAL_PRODUCT](state, product) {
            state.original = product;
        },

        [type.SET_PRODUCT_ATTR](state, { key, value }) {
            state.product[key] = value;
        },

        [type.SET_VARIANT_ATTR](state, { index, key, value }) {
            Vue.set(state.product.variants[index], key, value);
        },

        [type.ADD_VARIANT](state, variant) {
            state.product.variants.push(variant);
        },

        [type.REMOVE_VARIANT](state, index) {
            state.product.variants.splice(index, 1);
        },

        [type.SET_OUTLET_INVENTORY_ATTR](
            state,
            { variant_index, inventory_index, key, value },
        ) {
            state.product.variants[variant_index].stock.inventories[
                inventory_index
            ][key] = value;
        },

        [type.ADD_OUTLET_INVENTORY](state, { variant_index, stock_inventory }) {
            state.product.variants[variant_index].stock.inventories.push(
                stock_inventory,
            );
        },

        [type.ADD_COMPOSITE_COMPONENT](state, { variant_index, component }) {
            state.product.variants[variant_index].stock.composites.push(
                component,
            );
        },

        [type.REMOVE_COMPOSITE_COMPONENT](state, { variant_index, index }) {
            state.product.variants[variant_index].stock.composites.splice(
                index,
                1,
            );
        },

        [type.UPDATE_COMPOSITE_COMPONENT](
            state,
            { variant_index, index, key, value },
        ) {
            Vue.set(
                state.product.variants[variant_index].stock.composites[index],
                key,
                value,
            );
        },
    },
    actions: {
        async fetchProduct({ commit, dispatch }, id) {
            startLoading(dispatch, 'fetch-product');
            const route = Laroute.route('products.show', { id });

            try {
                const { data } = await axios.get(route);

                if (_.isNull(data.inventory_type)) {
                    data.inventory_type = TYPE_INVENTORY;
                }

                commit(type.SET_PRODUCT, await mapToClientObject(data));
                commit(type.SET_ORIGINAL_PRODUCT, _.cloneDeep(data));
            } catch (e) {
                throw e;
            }

            endLoading(dispatch, 'fetch-product');
        },

        setVariantAttr({ commit }, { index, key, value }) {
            commit(type.SET_VARIANT_ATTR, { index, key, value });
        },

        setProductAttribute({ commit }, { key, value }) {
            commit(type.SET_PRODUCT_ATTR, { key, value });
        },

        addVariant({ commit, state }) {
            const variantTempId = state.decrementalId;
            state.decrementalId--;
            const stockTempId = state.decrementalId;
            state.decrementalId--;

            commit(
                type.ADD_VARIANT,
                Object.assign(
                    { id: variantTempId, stock: { id: stockTempId } },
                    _.cloneDeep(variantModel),
                ),
            );
        },

        removeVariant({ commit }, index) {
            commit(type.REMOVE_VARIANT, index);
        },

        setOutletInventoryAttr(
            { commit, getters, state },
            { outlet_id, variant_id, key, value },
        ) {
            let stock_inventory = getters.findOutletInventory(
                outlet_id,
                variant_id,
            );

            const variant = getters.findVariant(variant_id);
            const variant_index = getters.findVariantIndex(variant_id);

            // if not exists create it
            if (_.isNil(stock_inventory)) {
                const inventoryTempId = state.decrementalId;
                state.decrementalId--;

                stock_inventory = {
                    id: inventoryTempId,
                    outlet_id,
                    quantity: null,
                    low_stock_level: null,
                    ideal_stock_level: null,
                    stock_id: variant.stock.id,
                };

                stock_inventory[key] = value;

                commit(type.ADD_OUTLET_INVENTORY, {
                    variant_index,
                    stock_inventory,
                });
            } else {
                const inventory_index = getters.findOutletInventoryIndex(
                    outlet_id,
                    variant_id,
                );

                commit(type.SET_OUTLET_INVENTORY_ATTR, {
                    inventory_index,
                    variant_index,
                    key,
                    value,
                });
            }
        },

        async createProduct(context, product) {
            const url = Laroute.route('products.create');

            try {
                const { data } = await axios.post(url, product);
                return data;
            } catch (e) {
                throw e;
            }
        },

        async updateProduct({ commit }, product) {
            const url = Laroute.route('products.update', { id: product.id });

            try {
                const { data } = await axios.put(
                    url,
                    mapToServerObject(product),
                );

                commit(type.SET_PRODUCT, await mapToClientObject(data));
                commit(type.SET_ORIGINAL_PRODUCT, _.cloneDeep(data));
            } catch (e) {
                throw e;
            }
        },

        addCompositeComponent(
            { commit, state },
            {
                variant_index,
                stock_id,
                composite_stock,
                composite_stock_id,
                composite_potion,
            },
        ) {
            const component = {
                id: state.decrementalId,
                stock_id,
                composite_stock,
                composite_stock_id,
                composite_potion,
            };

            state.decrementalId--;

            commit(type.ADD_COMPOSITE_COMPONENT, { variant_index, component });
        },

        updateCompositeComponent(
            { commit },
            { variant_index, index, key, value },
        ) {
            commit(type.UPDATE_COMPOSITE_COMPONENT, {
                variant_index,
                index,
                key,
                value,
            });
        },

        removeCompositeComponent({ commit }, { variant_index, index }) {
            commit(type.REMOVE_COMPOSITE_COMPONENT, { variant_index, index });
        },
    },
};



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/product.js